import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Avatar, Box, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import leonidImage from '@/assets/img/leonid.jpg';
import { useUser } from '@/modules/common/application/context/user';
import { useYbug } from '@/modules/common/application/context/ybug';
import Button from '@/modules/common/components/Button/Button';
import Markdown from '@/modules/common/components/Markdown';
import { MessageStatus, SenderType } from '@/modules/common/types';

import { ChatWindowMessage } from '../types/message';
interface ChatMessageProps {
  message: ChatWindowMessage;
}

export const ChatMessage = ({ message }: ChatMessageProps) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const ybugContext = useYbug();

  const openYbugFeedback = () => {
    if (ybugContext && ybugContext.Ybug) {
      if (user) {
        ybugContext.Ybug.setUser({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
        });
      }
      ybugContext.Ybug.open();
    }
  };

  const processMessageContent = (message: ChatWindowMessage) => {
    const ybugPattern = /\[App: Ybug Feedback Widget\]/;
    const hasYbugReference = ybugPattern.test(message.content);

    if (hasYbugReference) {
      return {
        ...message,
        content: message.content.replace(ybugPattern, '').trim(),
        showYbugButton: true,
      };
    }

    return {
      ...message,
      showYbugButton: false,
    };
  };

  const processedMessage = processMessageContent(message);
  const isUser = processedMessage.sender === SenderType.USER;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'start',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        gap: 1,
      }}
    >
      {!isUser && processedMessage.sender === SenderType.BOT && (
        <Tooltip title="Maia">
          <Avatar
            sx={{
              bgcolor: '#202648',
              width: 28,
              height: 28,
              mt: 0.5,
            }}
          >
            <AutoAwesomeIcon fontSize="inherit" sx={{ fontSize: '0.875rem' }} />
          </Avatar>
        </Tooltip>
      )}
      {!isUser && processedMessage.sender === SenderType.SUPPORT && (
        <Tooltip title="Leonid Vogel">
          <Avatar
            sx={{
              width: 28,
              height: 28,
              mt: 0.5,
            }}
            src={leonidImage}
          />
        </Tooltip>
      )}
      <Paper
        elevation={0}
        sx={{
          maxWidth: '80%',
          borderRadius: 4,
          px: 2,
          py: 1,
          backgroundColor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'white' : 'text.primary',
        }}
      >
        <Typography variant="body2">
          <Markdown>{processedMessage.content}</Markdown>
          {message.status === MessageStatus.IN_PROGRESS && !message.content && (
            <Box sx={{ display: 'inline-flex', ml: 1, verticalAlign: 'middle' }}>
              <CircularProgress size={16} thickness={4} />
            </Box>
          )}
        </Typography>
        {processedMessage.showYbugButton && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
            <Button variant="contained" onClick={openYbugFeedback} size="small">
              {t('modules.chat.chatMessage.openFeedbackForm')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
